/* assets/gcitypartners-repeat-revenue.js — GCITYPARTNERS-REPEAT-REVENUE-P2
 * Read-only child projection over existing customer-owned Professional Projects.
 * Uses the existing LittleGWebSession native bearer for the private Project read.
 * Suggests a follow-on review for completed work; it does not create projects/RFQs or claim future revenue.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-gcp-supplier-network]';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var API_PREFIX = '/v1/gcitypartners/';
  /** @typedef {Record<string, unknown>} ProjectRecord */
  /**
   * @typedef {Readonly<{
   *   mountAll: (scope?: ParentNode | null) => void,
   *   completed: (item: ProjectRecord | null | undefined) => boolean,
   *   followOnUrl: (item: ProjectRecord | null | undefined, root?: Element | null) => string
   * }>} RepeatRevenueProjection
   */
  /** @type {WeakSet<Element>} */
  const mounted = new WeakSet();
  /** @type {WeakMap<Element, number>} */
  const loadVersions = new WeakMap();
  /**
   * @type {Window & {
   *   GCityPartnersRepeatRevenue?: RepeatRevenueProjection,
   *   LittleGWebSession?: Readonly<{
   *     authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
   *   }> | null
   * }}
   */
  const repeatWindow = window;
  const existingProjection = repeatWindow.GCityPartnersRepeatRevenue;

  if (existingProjection && typeof existingProjection.mountAll === 'function') {
    existingProjection.mountAll(document);
    return;
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function isZh() {
    return /^zh/i.test(clean(document.documentElement.lang));
  }
  /**
   * @param {string} zh
   * @param {string} en
   */
  function words(zh, en) {
    return isZh() ? zh : en;
  }
  /**
   * @template {keyof HTMLElementTagNameMap} T
   * @param {T} tag
   * @param {string} [className]
   * @param {unknown} [text]
   * @returns {HTMLElementTagNameMap[T]}
   */
  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }
  /** @param {Element} root */
  function endpoint(root) {
    var raw = clean(root.getAttribute('data-projects-endpoint'));
    if (!raw) return '';
    try {
      var url = new URL(raw, API_ORIGIN);
      return url.origin === API_ORIGIN && url.pathname.indexOf(API_PREFIX) === 0
        ? url.toString()
        : '';
    } catch (_error) {
      return '';
    }
  }
  function nativeSession() {
    const session = repeatWindow.LittleGWebSession;
    return session && typeof session.authenticatedFetch === 'function' ? session : null;
  }
  /**
   * @param {Element} root
   * @returns {Promise<ProjectRecord[]>}
   */
  async function projects(root) {
    const url = endpoint(root);
    const owner = nativeSession();
    if (!url || !owner) {
      throw new Error('project_service_unavailable');
    }

    const join = url.indexOf('?') >= 0 ? '&' : '?';
    const response = await owner.authenticatedFetch(url + join + 'limit=50', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    /** @type {unknown} */
    const payload = await response.json().catch(function () {
      return {};
    });

    if (!response.ok || !isProjectRecord(payload) || payload.ok !== true) {
      throw new Error('project_read_failed');
    }
    const items = payload.items;
    if (!Array.isArray(items) || !items.every(isProjectRecord)) {
      throw new Error('invalid_project_response');
    }

    return items;
  }
  /** @param {ProjectRecord | null | undefined} item */
  function completed(item) {
    return (
      clean(item && item.status).toLowerCase() === 'completed' ||
      clean(item && item.commercial_status).toLowerCase() === 'completed'
    );
  }
  /** @param {ProjectRecord | null | undefined} item */
  function followOnLabel(item) {
    var type = clean(item && item.delivery_type).toLowerCase();
    if (type === 'report')
      return words('更新 / 重測 / 新一期報告', 'Update / resurvey / next report cycle');
    return words('續期 / 下一階段 / 延伸服務', 'Renewal / next phase / extended service');
  }
  /**
   * @param {ProjectRecord | null | undefined} item
   * @param {Element | null} [root]
   */
  function followOnUrl(item, root) {
    const context = root || document.querySelector(ROOT);
    const localeRoot = clean(context && context.getAttribute('data-gcp-locale-root'));
    const prefix = localeRoot.replace(/\/$/, '');
    const path = prefix + '/pages/gcitypartners';
    const url = new URL(path, window.location.origin);

    if (url.origin !== window.location.origin) {
      throw new Error('invalid_follow_on_route');
    }

    url.searchParams.set('source', 'gcitypartners-repeat-revenue');
    url.searchParams.set('intent', 'professional-review');
    url.searchParams.set('stage', 'intake');
    url.searchParams.set('delivery_type', 'manual-review');

    const id = clean(item && item.job_id);
    if (/^[A-Za-z0-9._:-]{3,160}$/.test(id)) {
      url.searchParams.set('source_job', id);
    }

    url.hash = 'gcp-brief-form';
    return url.toString();
  }
  /**
   * @param {Element} root
   * @param {ProjectRecord[]} items
   */
  function render(root, items) {
    root.querySelectorAll('[data-gcp-repeat-revenue]').forEach(function (panel) {
      panel.remove();
    });

    const eligible = items
      .filter(function (item) {
        return completed(item) && /^[A-Za-z0-9._:-]{3,160}$/.test(clean(item && item.job_id));
      })
      .slice(0, 6);
    if (!eligible.length) return;

    var panel = make('section', 'gcp-supplier-network__panel');
    panel.setAttribute('data-gcp-repeat-revenue', '');
    var head = make('div', 'gcp-supplier-network__panel-head');
    var heading = make('div');
    heading.appendChild(make('span', '', '04'));
    heading.appendChild(
      make(
        'h3',
        '',
        words('把完成項目變成下一筆生意', 'Turn completed work into the next opportunity')
      )
    );
    head.appendChild(heading);
    head.appendChild(make('small', '', words('只顯示既有已完成項目', 'Completed projects only')));
    panel.appendChild(head);

    panel.appendChild(
      make(
        'p',
        'gcp-supplier-network__fine',
        words(
          '以下只是後續服務提醒，不代表客戶一定續約，也不會計入未來收入。需要時仍從既有 GCityPartners Brief 開新項目。',
          'These are follow-on service prompts only. They do not imply renewal or count as future revenue. Start a new project through the existing GCityPartners Brief when needed.'
        )
      )
    );

    var list = make('div', 'gcp-supplier-network__rfq-list');
    eligible.forEach(function (item) {
      var card = make('article', 'gcp-supplier-network__rfq');
      const title =
        typeof item.title === 'string' && item.title.trim()
          ? item.title.trim()
          : words('已完成項目', 'Completed project');
      card.appendChild(make('strong', '', title));
      var meta = make('div', 'gcp-supplier-network__meta');
      meta.appendChild(make('span', '', followOnLabel(item)));
      if (clean(item.updated_at))
        meta.appendChild(make('span', '', clean(item.updated_at).slice(0, 10)));
      card.appendChild(meta);
      var actions = make('div', 'gcp-supplier-network__actions');
      var link = make('a', 'gcp-dashboard__button', words('開始後續需求', 'Start follow-on brief'));
      link.href = followOnUrl(item, root);
      actions.appendChild(link);
      card.appendChild(actions);
      list.appendChild(card);
    });
    panel.appendChild(list);
    root.appendChild(panel);
  }
  /** @param {Element | null} root */
  async function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);

    const version = (loadVersions.get(root) || 0) + 1;
    loadVersions.set(root, version);

    root.querySelectorAll('[data-gcp-repeat-revenue]').forEach(function (panel) {
      panel.remove();
    });
    root.setAttribute('data-gcp-repeat-state', 'loading');

    try {
      const items = await projects(root);
      if (loadVersions.get(root) !== version || !root.isConnected) return;

      render(root, items);
      root.setAttribute('data-gcp-repeat-state', 'ready');
    } catch (_error) {
      if (loadVersions.get(root) !== version || !root.isConnected) return;
      root.setAttribute('data-gcp-repeat-state', 'unavailable');
    }
  }
  /** @param {ParentNode | null} [scope] */
  function mountAll(scope) {
    (scope || document).querySelectorAll(ROOT).forEach(function (root) {
      void mount(root);
    });
  }
  /** @param {ParentNode | null} [scope] */
  function refresh(scope) {
    (scope || document).querySelectorAll(ROOT).forEach(function (root) {
      mounted.delete(root);
      void mount(root);
    });
  }

  repeatWindow.GCityPartnersRepeatRevenue = Object.freeze({
    mountAll: mountAll,
    completed: completed,
    followOnUrl: followOnUrl,
  });
  document.addEventListener('littleg:web-session-change', function () {
    refresh(document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target instanceof Element ? event.target : document);
  });
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  else mountAll(document);
})(window, document);
